import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import {
  Box,
  Typography,
  Card,
  Alert,
  Snackbar,
  Divider,
} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import GroupIcon from "@mui/icons-material/Group";

import SoftBox from "components/SoftBox";
import SoftSelect from "components/SoftSelect";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DataTable from "examples/Tables/DataTable";

import { updateWorkCalendar } from "api/workCalendarApi";
import { getBranches } from "api/branches";

import InterviewSelected from "layouts/interviewSelected";

import { useAuth } from "AuthContext";
import { hasPermission } from "permissionUtils";
import WorkCalenderTable from "../workCalenderTable";

/* ===================== CONSTANTS ===================== */

const daysOfWeek = [
  { value: "Sunday", label: "Sunday" },
  { value: "Monday", label: "Monday" },
  { value: "Tuesday", label: "Tuesday" },
  { value: "Wednesday", label: "Wednesday" },
  { value: "Thursday", label: "Thursday" },
  { value: "Friday", label: "Friday" },
  { value: "Saturday", label: "Saturday" },
];

const viewModeOptions = [
  {
    value: "branchwise",
    label: (
      <Box display="flex" alignItems="center">
        <LocationCityIcon fontSize="small" sx={{ mr: 1 }} />
        Branchwise
      </Box>
    ),
  },
  {
    value: "userwise",
    label: (
      <Box display="flex" alignItems="center">
        <GroupIcon fontSize="small" sx={{ mr: 1 }} />
        Userwise (Upload Excel)
      </Box>
    ),
  },
];

/* ===================== COMPONENT ===================== */

const WorkCalendar = () => {
  const { userPermission = [] } = useAuth();

  const canView = hasPermission(
    "Settings.LeaveTracker.WorkCalender",
    userPermission
  );

  const canUpdate = hasPermission(
    "Settings.LeaveTracker.WorkCalender.UpdateWorkCalender",
    userPermission
  );

  /* ---------------- STATE ---------------- */
  const [viewMode, setViewMode] = useState(viewModeOptions[0]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [excelFile, setExcelFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* ---------------- FETCH BRANCHES ---------------- */
  useEffect(() => {
    if (!canView) return;
    fetchBranches();
  }, [canView]);

  const fetchBranches = async () => {
    setBranchesLoading(true);
    try {
      const response = await getBranches(1, 100);
      setBranches(
        response?.data?.map((b) => ({
          value: b.id.toString(),
          label: b.name,
        })) || []
      );
    } catch {
      showSnackbar("Error fetching branches", "error");
    } finally {
      setBranchesLoading(false);
    }
  };

  /* ---------------- HELPERS ---------------- */
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  /* ---------------- EXCEL UPLOAD ---------------- */
  const handleExcelUpload = (e) => {
    if (!canUpdate) return;

    const file = e.target.files[0];
    if (!file) return;

    setExcelFile(file);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: "binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

      const formatted = data.slice(1).map((row) => ({
        employeeCode: row[0],
        weekendOff: row[1],
      }));

      setExcelData(formatted);
    };

    reader.readAsBinaryString(file);
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async () => {
    if (!canUpdate) return;

    setLoading(true);
    try {
      const formData = new FormData();

      if (viewMode.value === "branchwise") {
        if (!selectedBranch || selectedDays.length === 0) {
          showSnackbar("Please select branch and weekend off day", "error");
          setLoading(false);
          return;
        }

        formData.append("mode", "branchwise");
        formData.append("branch_id", selectedBranch.value);
        selectedDays.forEach((day) => formData.append("weekend_off[]", day.value));
      } else {
        if (!excelFile) {
          showSnackbar("Please upload Excel file", "error");
          setLoading(false);
          return;
        }

        formData.append("mode", "userwise");
        formData.append("file", excelFile);
      }

      await updateWorkCalendar(formData);
      showSnackbar("Work calendar updated successfully");

      setSelectedBranch(null);
      setSelectedDays([]);
      setExcelFile(null);
      setExcelData([]);
    } catch {
      showSnackbar("Error updating work calendar", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- TABLE ---------------- */
  const tableData = {
    columns: [
      { Header: "Employee Code", accessor: "employeeCode" },
      { Header: "Weekend Off", accessor: "weekendOff" },
    ],
    rows: excelData.map((r, i) => ({ ...r, id: i })),
  };

  if (!canView) return null;

  return (
    <SoftBox>

      {/* ================= CARD 1 : FORM ================= */}
      <Card sx={{ px: 2, mb: 3 ,pt:2 }}>
        <SoftTypography variant="h5" mb={1}>
          📅 Work Calendar
        </SoftTypography>

        <SoftTypography variant="h6">
          Choose View Mode
        </SoftTypography>

        <SoftSelect
          value={viewMode}
          onChange={(opt) => {
            setViewMode(opt);
            setSelectedBranch(null);
            setSelectedDays([]);
            setExcelData([]);
            setExcelFile(null);
          }}
          options={viewModeOptions}
        />

        {viewMode.value === "branchwise" && (
          <Box display="flex" gap={2} mt={2}>
            <Box width="50%">
              <SoftTypography variant="h6" >
                Branch
              </SoftTypography>
              <SoftSelect
                value={selectedBranch}
                onChange={setSelectedBranch}
                options={branches}
                isLoading={branchesLoading}
                isDisabled={branchesLoading}
              />
            </Box>

            <Box width="50%">
              <SoftTypography variant="h6">
                Select Days
              </SoftTypography>
              <SoftSelect
                value={selectedDays}
                onChange={(options) => setSelectedDays(options || [])}
                options={daysOfWeek}
                isMulti
                closeMenuOnSelect={false}
              />
            </Box>
          </Box>
        )}

        {viewMode.value === "userwise" && (
          <Box my={2}>
            <SoftButton
              component="label"
              variant="outlined"
              color="info"
              startIcon={<UploadFileIcon />}
              disabled={!canUpdate}
            >
              Upload Excel File
              <input
                type="file"
                accept=".xlsx, .xls"
                hidden
                onChange={handleExcelUpload}
              />
            </SoftButton>

            {excelFile && (
              <SoftTypography variant="caption" sx={{ ml: 2 }}>
                Selected: {excelFile.name}
              </SoftTypography>
            )}
          </Box>
        )}

        <Box my={3}>
          <SoftButton
            variant="gradient"
            color="info"
            onClick={handleSubmit}
            disabled={!canUpdate || loading}
            loading={loading}
          >
            Update Work Calendar
          </SoftButton>
        </Box>
      </Card>

      {/* ================= CARD 2 : INTERVIEW SELECTED ================= */}
      <div sx={{ mb: 3 }}>
        <WorkCalenderTable embedded />
      </div>

      {/* ================= CARD 3 : TABLE ================= */}
      {excelData.length > 0 && (
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" mb={2}>
            Uploaded Data Preview
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <DataTable
            table={tableData}
            showTotalEntries
            entriesPerPage={{ defaultValue: 10 }}
          />
        </Card>
      )}

      {/* ================= SNACKBAR ================= */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SoftBox>
  );
};

export default WorkCalendar;
